/**
 * Copy for `RequestSourcesAccessCta`, per thing the data-room pass unlocks.
 *
 * "The data room is a pass to access all claims and proof points" (owner rule,
 * 2026-10-19), and since 2026-10-23 also to diligence itself: "You shouldn't
 * have access to the generate DD report function if you didn't have prior
 * access to the data room to make your DD." One request flow, one component;
 * only the sentence about what the pass opens differs.
 */
import type { DataRoomRequestState as RequestState } from '@/lib/dd/dd-entry';

/** What the investor is asking the data room for. */
export type RequestAccessPurpose = 'sources' | 'diligence' | 'report';

const COPY: Record<RequestAccessPurpose, Record<RequestState, string> & { button: string }> = {
  sources: {
    pending: 'Data-room request sent. Quotes, sources and claims appear once the founder approves.',
    rejected: 'The founder declined data-room access, so quotes, sources and claims stay private.',
    approved: 'Your data-room access is no longer active, so quotes, sources and claims are hidden.',
    none: 'Quotes, sources and claims behind this score are visible with data-room access.',
    button: 'Request data-room access to view sources',
  },
  diligence: {
    pending: 'Data-room request sent. Due diligence opens once the founder approves.',
    rejected: 'The founder declined data-room access, so due diligence cannot start.',
    approved: 'Your data-room access is no longer active, so due diligence cannot start.',
    none: 'Due diligence starts with access to the founder’s data room.',
    button: 'Request data-room access',
  },
  report: {
    pending: 'Data-room request sent. The report can be generated once the founder approves.',
    rejected: 'The founder declined data-room access, so this report cannot be generated.',
    approved: 'Your data-room access is no longer active, so this report cannot be generated.',
    none: 'Generating this report needs access to the founder’s data room.',
    button: 'Request data-room access',
  },
};

export function requestAccessCopy(
  purpose: RequestAccessPurpose,
  state: RequestState,
): { message: string; button: string } {
  const c = COPY[purpose];
  return { message: c[state], button: c.button };
}
